import { cn } from '@/lib/cn';
import { Toolbar } from '@heidi/editor/ui';
import * as Dropdown from '@radix-ui/react-dropdown-menu';
import { useEditorState, type Editor } from '@tiptap/react';
import {
  ChevronDown,
  Heading1,
  List,
  ListOrdered,
  Pilcrow,
  TextQuote,
  type LucideIcon,
} from 'lucide-react';
import { useState } from 'react';
import { defineMessages, useIntl, type MessageDescriptor } from 'react-intl';

const messages = defineMessages({
  textStyle: {
    defaultMessage: 'Text style',
    id: 'ctwmLJkglX',
    description: 'Tooltip of the selection-menu dropdown that changes the block type',
  },
  normalText: {
    defaultMessage: 'Normal text',
    id: 'jCzMfFx+KA',
    description: 'Text style option that turns the selection into a plain paragraph',
  },
  headline: {
    defaultMessage: 'Headline',
    id: 'QVpV67V0xy',
    description: 'Text style option that turns the selection into a heading',
  },
  quote: {
    defaultMessage: 'Quote',
    id: 'tBH7OyHrrw',
    description: 'Text style option that turns the selection into a block quote',
  },
  bulletList: {
    defaultMessage: 'Bullet list',
    id: 'stGpH+yZos',
    description: 'Text style option that turns the selection into a bullet list',
  },
  numberedList: {
    defaultMessage: 'Numbered list',
    id: '7FfDp0OOSg',
    description: 'Text style option that turns the selection into a numbered list',
  },
});

interface TextStyleOption {
  id: string;
  icon: LucideIcon;
  message: MessageDescriptor;
  isActive: boolean;
  onSelect: () => void;
}

/**
 * Builds the block-type options supported by THIS editor's schema — template
 * and note editors register slightly different node sets, so unsupported
 * styles (e.g. quote where blockquote isn't installed) simply don't appear.
 */
const useTextStyleOptions = (editor: Editor): TextStyleOption[] =>
  useEditorState({
    editor,
    selector: (ctx): TextStyleOption[] => {
      const { nodes } = ctx.editor.schema;
      const options: TextStyleOption[] = [
        {
          id: 'paragraph',
          icon: Pilcrow,
          message: messages.normalText,
          isActive:
            ctx.editor.isActive('paragraph') &&
            !ctx.editor.isActive('bulletList') &&
            !ctx.editor.isActive('orderedList') &&
            !ctx.editor.isActive('blockquote'),
          onSelect: () =>
            ctx.editor.chain().focus().liftListItem('listItem').setParagraph().run(),
        },
      ];
      if (nodes.heading) {
        options.push({
          id: 'heading',
          icon: Heading1,
          message: messages.headline,
          isActive: ctx.editor.isActive('heading'),
          onSelect: () =>
            ctx.editor
              .chain()
              .focus()
              .liftListItem('listItem')
              .setHeading({ level: 1 })
              .run(),
        });
      }
      if (nodes.blockquote) {
        options.push({
          id: 'blockquote',
          icon: TextQuote,
          message: messages.quote,
          isActive: ctx.editor.isActive('blockquote'),
          onSelect: () => ctx.editor.chain().focus().toggleBlockquote().run(),
        });
      }
      if (nodes.bulletList) {
        options.push({
          id: 'bulletList',
          icon: List,
          message: messages.bulletList,
          isActive: ctx.editor.isActive('bulletList'),
          onSelect: () => ctx.editor.chain().focus().toggleBulletList().run(),
        });
      }
      if (nodes.orderedList) {
        options.push({
          id: 'orderedList',
          icon: ListOrdered,
          message: messages.numberedList,
          isActive: ctx.editor.isActive('orderedList'),
          onSelect: () => ctx.editor.chain().focus().toggleOrderedList().run(),
        });
      }
      return options;
    },
  }) ?? [];

/**
 * Selection-menu dropdown (selection → Ask Heidi v5.1) that turns the
 * highlighted block into another style: normal text, headline, quote, lists.
 * The trigger shows the active style's icon. Content renders without a portal
 * so the bubble menu stays open while the flyout has focus (same pattern as
 * the AI Rewrite submenu).
 */
export const TextStyleDropdown = ({
  editor,
  iconSize = 16,
}: {
  editor: Editor;
  iconSize?: number;
}) => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const options = useTextStyleOptions(editor);

  if (options.length <= 1) return null;

  const ActiveIcon = options.find((option) => option.isActive)?.icon ?? Pilcrow;

  return (
    <Dropdown.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dropdown.Trigger asChild>
        <Toolbar.Button
          tooltip={intl.formatMessage(messages.textStyle)}
          type="button"
          size="md"
          iconOnly={false}
          className="gap-0.5"
          data-testid="text-menu-text-style"
        >
          <ActiveIcon size={iconSize} />
          <ChevronDown size={12} className="text-text-secondary" />
        </Toolbar.Button>
      </Dropdown.Trigger>
      <Dropdown.Content
        side="bottom"
        align="start"
        sideOffset={8}
        onMouseDown={(e) => {
          // Keep the editor selection (and the bubble menu) alive while
          // interacting with the flyout.
          e.preventDefault();
        }}
        className="z-50 flex min-w-44 flex-col gap-0.5 rounded-lg border border-border bg-surface-elevated p-2 shadow-lg"
      >
        {options.map(({ id, icon: Icon, message, isActive, onSelect }) => (
          <Dropdown.Item key={id} asChild>
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onSelect();
              }}
              className={cn(
                'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-text-primary outline-none hover:bg-background-secondary focus-visible:bg-background-secondary',
                isActive && 'bg-background-secondary'
              )}
              data-testid={`text-style-option-${id}`}
            >
              <Icon size={14} className="shrink-0 text-text-secondary" />
              {intl.formatMessage(message)}
            </button>
          </Dropdown.Item>
        ))}
      </Dropdown.Content>
    </Dropdown.Root>
  );
};
